/**
 * Client-side evaluator: resolved query -> predicate over cached ItemLite rows.
 * Pure: safe to import from the browser (`@plannello/core/query/toPredicate`).
 *
 * Differences from toSql, by design: free text matches the title and current key only (the
 * server also searches descriptions with full-text search and matches historic keys; it searches the
 * original title only, while `extraText` lets the client also match what the reader sees, e.g. a
 * translated title), and
 * `is:blocked` only sees blockers present in the client cache.
 */
import { type ItemLite, isClosedCategory, priorityUrgency, type StatusCategory } from "@plannello/shared";
import { compareRank } from "../ranking.ts";
import { itemKeyPrefix } from "./keyPrefix.ts";
import type { Condition, ResolvedQuery, ResolvedSort } from "./resolve.ts";

export interface PredicateContext {
  statusCategory: (statusId: string) => StatusCategory | undefined;
  getItem: (id: string) => ItemLite | undefined;
  hasChildren: (id: string) => boolean;
  /** Further text of an item that free text also matches (the translated title the reader sees). */
  extraText?: (item: ItemLite) => string | undefined;
}

/** Builds a PredicateContext from the cached items and the statuses of the schema. */
export function createPredicateContext(
  items: Iterable<ItemLite>,
  statuses: readonly { id: string; category: StatusCategory }[],
  extraText?: (item: ItemLite) => string | undefined,
): PredicateContext {
  const byId = new Map<string, ItemLite>();
  const parents = new Set<string>();
  for (const item of items) {
    byId.set(item.id, item);
    if (item.parentId) parents.add(item.parentId);
  }
  const categories = new Map(statuses.map((s) => [s.id, s.category]));
  return {
    statusCategory: (id) => categories.get(id),
    getItem: (id) => byId.get(id),
    hasChildren: (id) => parents.has(id),
    extraText,
  };
}

/** Lowercase and strip diacritics, approximating SQL `public.pt_unaccent(lower(x))`. */
export function normalizeText(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");
}

function test(c: Condition, item: ItemLite, ctx: PredicateContext): boolean {
  switch (c.t) {
    case "in": {
      const value = (item[c.column] ?? null) as string | number | null;
      return c.values.includes(value);
    }
    case "set": {
      const set = item[c.column];
      return (c.empty && set.length === 0) || c.values.some((v) => set.includes(v));
    }
    case "range": {
      const raw = item[c.column];
      if (raw === null) return false;
      if (c.kind === "date") return (c.gte === undefined || raw >= c.gte) && (c.lt === undefined || raw < c.lt);
      const t = Date.parse(raw);
      return (c.gte === undefined || t >= Date.parse(c.gte)) && (c.lt === undefined || t < Date.parse(c.lt));
    }
    case "blocked":
      return item.blockedByIds.some((id) => {
        const blocker = ctx.getItem(id);
        const category = blocker && ctx.statusCategory(blocker.statusId);
        return category !== undefined && !isClosedCategory(category);
      });
    case "hasChildren":
      return ctx.hasChildren(item.id);
    case "text": {
      const needle = normalizeText(c.text);
      if (normalizeText(item.title).includes(needle)) return true;
      const extra = ctx.extraText?.(item);
      if (extra && normalizeText(extra).includes(needle)) return true;
      const prefix = itemKeyPrefix(c.text);
      return prefix ? item.key.startsWith(prefix) : item.key === c.text.toUpperCase();
    }
    case "or":
      return c.conditions.some((sub) => evaluate(sub, item, ctx));
  }
}

function evaluate(c: Condition, item: ItemLite, ctx: PredicateContext): boolean {
  return test(c, item, ctx) !== c.negate;
}

export function toPredicate(query: ResolvedQuery, ctx: PredicateContext): (item: ItemLite) => boolean {
  return (item) => {
    if ((item.archivedAt !== null) !== (query.archived === "only")) return false;
    return query.conditions.every((c) => evaluate(c, item, ctx));
  };
}

function groupPosition(item: ItemLite, positions: Record<string, number>): number | null {
  let min: number | null = null;
  for (const id of item.labelIds) {
    const p = positions[id];
    if (p !== undefined && (min === null || p < min)) min = p;
  }
  return min;
}

/** Comparator matching toSql's ORDER BY for the given sort (rank, id as final tie-breakers). */
export function compareItems(sort: readonly ResolvedSort[]): (a: ItemLite, b: ItemLite) => number {
  const keys: readonly ResolvedSort[] = sort.length > 0 ? sort : [{ key: "rank", dir: "asc" }];
  return (a, b) => {
    for (const { key, dir, positions } of keys) {
      const sign = dir === "asc" ? 1 : -1;
      let d = 0;
      switch (key) {
        case "rank":
          d = compareRank(a, b);
          break;
        case "priority":
          d = priorityUrgency(a.priority) - priorityUrgency(b.priority);
          break;
        case "created":
          d = Date.parse(a.createdAt) - Date.parse(b.createdAt);
          break;
        case "updated":
          d = Date.parse(a.updatedAt) - Date.parse(b.updatedAt);
          break;
        case "due":
          // Items without a due date sort last in both directions.
          if (a.dueDate === b.dueDate) d = 0;
          else if (a.dueDate === null) return 1;
          else if (b.dueDate === null) return -1;
          else d = a.dueDate < b.dueDate ? -1 : 1;
          break;
        case "number":
          d = a.number - b.number;
          break;
        case "title":
          d = a.title.localeCompare(b.title);
          break;
        case "group": {
          // Label position within the group; items without a label of it sort last in both directions.
          if (!positions) break;
          const pa = groupPosition(a, positions);
          const pb = groupPosition(b, positions);
          if (pa === pb) d = 0;
          else if (pa === null) return 1;
          else if (pb === null) return -1;
          else d = pa < pb ? -1 : 1;
          break;
        }
      }
      if (d !== 0) return d * sign;
    }
    return compareRank(a, b);
  };
}
